import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { userService } from '../services/userService';
import { supabase } from '../lib/supabase';
import { resolveChAccess } from '../contenthub/lib/chAccess';
import type { UserRole } from '../types/user';
import { log } from '../lib/logger';

interface ProtectedRouteProps {
  children: React.ReactNode;
  role?: 'admin' | 'agent';
  requireRole?: boolean;
}

export function ProtectedRoute({ children, role, requireRole = false }: ProtectedRouteProps) {
  const { user, loading: authLoading } = useAuth();
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAccess = async () => {
      if (authLoading) {
        return;
      }

      if (!user) {
        setIsRedirecting(true);
        navigate('/login', { replace: true });
        return;
      }

      try {
        // Check admin/user role FIRST - admins should never be redirected
        const roleData = await userService.getCurrentUserRole();

        if (roleData) {
          // User has admin/user role - allow access to main app
          setUserRole(roleData);

          // Check role match if specified
          if (role && roleData.role !== role) {
            log.debug('[ProtectedRoute] Wrong role, access denied');
            setIsRedirecting(true);
            navigate('/', { replace: true });
            return;
          }

          setLoading(false);
          return;
        }

        // No admin/user role, check if user is an influencer
        const { data: influencerAuth } = await supabase
          .from('influencer_auth')
          .select('id, status')
          .eq('auth_user_id', user.id)
          .eq('status', 'active')
          .maybeSingle();

        if (influencerAuth) {
          // User is an influencer (and not admin) - redirect to influencer portal
          log.debug('[ProtectedRoute] Detected influencer, redirecting to portal');
          setIsRedirecting(true);
          navigate('/creatorshub/portal', { replace: true });
          return;
        }

        // No main-app role and not an influencer — check if CH client/talent
        const chAccess = await resolveChAccess();
        if (chAccess.hasAccess) {
          log.debug('[ProtectedRoute] CH-only user, redirecting to contenthub');
          setIsRedirecting(true);
          navigate('/contenthub', { replace: true });
          return;
        }

        log.debug('[ProtectedRoute] No role found, redirecting to login');
        setIsRedirecting(true);
        navigate('/login', { replace: true });
      } catch (error) {
        log.error('Error checking access:', error);
        setIsRedirecting(true);
        navigate('/login', { replace: true });
      }
    };

    checkAccess();
  }, [user, authLoading, role, navigate]);

  if (authLoading || loading || isRedirecting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!userRole) {
    return null;
  }

  return <>{children}</>;
}
